/**
 * Shared postMessage protocol types for Host <-> iFrame communication.
 *
 * The host script runs on the WordPress page and manages the iframe element,
 * overlay, and fixed-element adjustments.
 *
 * The iframe content runs inside the iframe and contains the React chat app.
 */

/**
 * Unique prefix for all messages to avoid conflicts with other scripts.
 */
export const MESSAGE_PREFIX = 'dhsv100-ai-chat' as const;

// ---------------------------------------------------------------------------
// Morph / context types (additive, all optional on the wire)
// ---------------------------------------------------------------------------

/** Rect in host layout-viewport CSS px (`getBoundingClientRect()`). */
export interface HostRect {
	left: number;
	top: number;
	width: number;
	height: number;
	/** Effective corner radius in px, already clamped to min(width,height)/2. */
	radius: number;
}

/** Rect without a corner radius (text, icon and key-hint boxes). */
export type HostBox = Omit< HostRect, 'radius' >;

/** Visual skin of the origin, so the morph surface starts pixel-identical. */
export interface HostOriginSkin {
	/** Computed background of the origin. */
	background: string;
	/** Inset ring as box-shadow; 'none' if absent. */
	ring?: string;
	/** Backdrop blur of the origin in px. 0 = none. */
	backdropBlurPx?: number;
	backdropSaturate?: number;
}

/** What flies from the origin into the panel (ghost-text handoff). */
export interface HostOriginSnapshot {
	text?: {
		value: string;
		rect: HostBox;
		fontSizePx: number;
		color: string;
		/** true: placeholder text; false: typed text. */
		isPlaceholder: boolean;
	};
	icon?: {
		kind: 'sparkle' | 'search';
		rect: HostBox;
		color: string;
		/**
		 * Additive: an image icon (the launcher's avatar). Set only for morphs
		 * the iframe starts from its own launcher; the ghost and the composer
		 * slot then render this image instead of `kind`.
		 */
		src?: string;
	};
	keyHint?: { label: string; rect: HostBox };
}

export type EntrySource =
	| 'nav'
	| 'stage'
	| 'inline'
	| 'bubble'
	| 'speech'
	| 'pill'
	| 'anfrage'
	| 'api';

export type MorphPlacement = 'center' | 'corner' | 'sheet';

export interface MorphMotionFlags {
	reducedMotion: boolean;
	lite: boolean;
	snap: boolean;
	slow?: number;
	/** Host already played the anticipation on the origin. */
	pressed?: boolean;
}

export interface HostMorphOpen {
	presentation: 'morph';
	placement?: MorphPlacement;
	originRect: HostRect;
	/** Offset the iframe subtracts (visualViewport.offsetLeft/Top). */
	viewportOffset?: { x: number; y: number };
	skin?: HostOriginSkin;
	snapshot?: HostOriginSnapshot;
	motion?: MorphMotionFlags;
}

/** Fields the iframe can announce on 'ready' / 'chat-ready'. */
export type IframeCapability = 'morph' | 'draft-open' | 'context' | 'inline';

export const IFRAME_CAPABILITIES: readonly IframeCapability[] = [
	'morph',
	'draft-open',
	'context',
	'inline',
];

/**
 * Additive on an open command: the chat runs inline, in the page flow, inside
 * the iframe the host positioned over a form (`data-dhsv-chat-expand="inline"`).
 * Only sent when the iframe announced the `inline` capability.
 */
export interface HostInlineOpen {
	/** Corner radius of the open panel, px. */
	radius: number;
}

// ---------------------------------------------------------------------------
// iFrame -> Host messages
// ---------------------------------------------------------------------------

export interface IframeReadyMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'ready';
	/**
	 * Optional features this iframe supports. Absent on older iframes. The host
	 * only sends the matching optional fields when the capability is listed.
	 */
	capabilities?: IframeCapability[];
}

export interface IframeDrawerOpenMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'drawer-open';
	/**
	 * `inline` (additive): the chat opened inline after a host `inline` open;
	 * a host without an inline slot ignores it.
	 */
	variant: 'default' | 'sidebar' | 'fullscreen' | 'island' | 'inline';
	/**
	 * Additive: the open morphs out of the iframe's own launcher (sticky bar,
	 * its circle or the bubble). The host reports it as `morph: true` on the
	 * open event; older hosts ignore it.
	 */
	morph?: boolean;
}

export interface IframeDrawerCloseMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'drawer-close';
}

export interface IframeMessagesChangedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'messages-changed';
	messages: Array< {
		id: string;
		role: 'user' | 'assistant';
		content: string;
		timestamp: number;
	} >;
}

export interface IframeStatusChangedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'status-changed';
	status: 'idle' | 'streaming' | 'submitted' | 'error';
}

export interface IframeResponseDoneMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'response-done';
	/**
	 * Set to `true` when the visitor pressed Stop. Absent otherwise. Additive:
	 * a host that does not read it keeps working.
	 */
	aborted?: boolean;
}

export interface IframeChatReadyMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'chat-ready';
	/**
	 * Optional features this iframe supports. Absent on older iframes. The host
	 * only sends the matching optional fields when the capability is listed.
	 */
	capabilities?: IframeCapability[];
}

export interface IframeLauncherHeightMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'launcher-height';
	height: number;
}

/**
 * A tracking event of the iframe (message sent, answer, rating, button click).
 * `event` is the unprefixed name; `params` holds primitives only. The host
 * validates it against the allow-list of `tracking.ts` and builds the data
 * layer push itself. Additive: a host that does not know it ignores it.
 */
export interface IframeTrackMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'track';
	event: string;
	params: Record< string, string | number | boolean >;
}

/**
 * The active conversation id (a UUID) changed, or is announced before
 * `chat-ready`. Additive: a host that does not know it ignores it.
 */
export interface IframeConversationChangedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'conversation-changed';
	conversationId: string;
}

/**
 * First frame of the morph surface is painted at the origin rect. When no
 * host-side morph session is waiting (the morph started from the iframe's own
 * launcher), the host starts one that only tracks the landing, so the shrink
 * after the close waits for `morph-landed`.
 */
export interface IframeMorphStartedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'morph-started';
}

/** The surface landed back on the origin rect. */
export interface IframeMorphLandedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'morph-landed';
}

/**
 * Answer to the host's `inline-end`: the inline surface is gone, the iframe
 * can take its collapsed geometry again.
 */
export interface IframeInlineEndedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'inline-ended';
}

/** Settled (or closing) panel rect in viewport px. */
export interface IframeSurfaceRectMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'surface-rect';
	rect: HostRect;
	phase: 'settled' | 'closing';
}

export type IframeToHostMessage =
	| IframeReadyMessage
	| IframeDrawerOpenMessage
	| IframeDrawerCloseMessage
	| IframeMessagesChangedMessage
	| IframeStatusChangedMessage
	| IframeResponseDoneMessage
	| IframeChatReadyMessage
	| IframeLauncherHeightMessage
	| IframeTrackMessage
	| IframeConversationChangedMessage
	| IframeInlineEndedMessage
	| IframeMorphStartedMessage
	| IframeMorphLandedMessage
	| IframeSurfaceRectMessage;

// ---------------------------------------------------------------------------
// Host -> iFrame messages
// ---------------------------------------------------------------------------

export interface HostCloseRequestMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'close-request';
}

export interface HostExpandedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'expanded';
}

/**
 * Where a host-side open started, so the chat can grow from that point. Only
 * sent when the host knows the trigger element; opens that start inside the
 * iframe derive their own origin.
 */
export interface HostOpenOrigin {
	x: number;
	y: number;
}

/** Page/section context; never contains personal data or visitor free text. */
export interface ChatContext {
	pageId?: number;
	/** location.origin + pathname, no query/hash. */
	url?: string;
	lang?: string;
	sectionId?: string;
	/** Visible heading text, max 120 chars. */
	sectionTitle?: string;
	proactiveQuestionId?: string;
	entry?: EntrySource;
}

export interface HostOpenRequestMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'open-request';
	variant?: 'default' | 'sidebar' | 'fullscreen' | 'island';
	/**
	 * The host resolved `variant` (surface rule or explicit variant): the
	 * iframe opens exactly it, also from a sticky bar. Absent: the iframe
	 * picks from its own launcher state.
	 */
	explicitVariant?: boolean;
	/** Centre of the host-side trigger, in viewport (= iframe) coordinates. */
	origin?: HostOpenOrigin;
	/** Additive: morph presentation, only sent when the iframe has the 'morph' capability. */
	morph?: HostMorphOpen;
	/** Additive: run inline, only sent when the iframe has the 'inline' capability. */
	inline?: HostInlineOpen;
	/** Additive: prefill the composer without sending. */
	draft?: string;
	/** Additive: page/section context. */
	context?: ChatContext;
	/** Additive: where the open came from. */
	source?: EntrySource | string;
}

export interface HostOpenWithMessageMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'open-with-message';
	text: string;
	variant?: 'default' | 'sidebar' | 'fullscreen' | 'island';
	/** See {@link HostOpenRequestMessage.explicitVariant}. */
	explicitVariant?: boolean;
	startNewChat?: boolean;
	/** Centre of the host-side trigger, in viewport (= iframe) coordinates. */
	origin?: HostOpenOrigin;
	/** Additive: morph presentation, only sent when the iframe has the 'morph' capability. */
	morph?: HostMorphOpen;
	/** Additive: run inline, only sent when the iframe has the 'inline' capability. */
	inline?: HostInlineOpen;
	/** Additive: page/section context. */
	context?: ChatContext;
	/** Additive: where the open came from. */
	source?: EntrySource | string;
}

export interface HostSendMessageMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'send-message';
	text: string;
}

export interface HostNewChatMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'new-chat';
}

export interface HostClearHistoryMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'clear-history';
}

export interface HostSetViewMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'set-view';
	view: 'popover' | 'history';
}

/**
 * Safe-area insets measured on the host document.
 *
 * `env(safe-area-inset-*)` resolves to zero inside an iframe, so the chat
 * cannot read the notch or the home indicator itself and is told instead.
 * `bottom` is already reduced by whatever the on-screen keyboard covers, so
 * the receiver can apply it as padding without further arithmetic.
 */
export interface HostSetSafeAreaMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'set-safe-area';
	top: number;
	right: number;
	bottom: number;
	left: number;
	/**
	 * On-screen keyboard overlap in px for the compact panel on tablets, 0 when
	 * there is none. Additive: older receivers ignore it, and it is absent from
	 * messages sent by older hosts.
	 */
	keyboardInset?: number;
}

export type LauncherVisualState = 'bar' | 'bubble';

export interface HostSetLauncherStateMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'set-launcher-state';
	state: LauncherVisualState;
}

/**
 * The page the host document shows.
 *
 * The iframe is served from a fixed, page-independent URL so it can be cached,
 * which means its own request no longer knows the page.
 */
export interface HostPageContextMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'page-context';
	pageId: number;
	/** Additive: location.origin + pathname, no query/hash. */
	url?: string;
	/** Additive: document language, e.g. 'de-DE'. */
	lang?: string;
	/** Additive: current section id. */
	sectionId?: string;
	/**
	 * Additive: what this host supports. The iframe only morphs out of its
	 * own launcher when `launcher-morph` is listed (an older host would
	 * shrink the iframe under the folding surface).
	 */
	hostCapabilities?: HostCapability[];
}

/** Optional features a host announces on `page-context`. */
export type HostCapability = 'launcher-morph';

export const HOST_CAPABILITIES: readonly HostCapability[] = [
	'launcher-morph',
];

/**
 * Narrow an optional `hostCapabilities` field to the ones this build knows.
 * @param value
 */
export function sanitizeHostCapabilities( value: unknown ): HostCapability[] {
	if ( ! Array.isArray( value ) ) {
		return [];
	}
	return HOST_CAPABILITIES.filter( ( cap ) => value.includes( cap ) );
}

/** The origin moved or resized while the chat is open; a close lands there. */
export interface HostOriginRectMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'origin-rect';
	rect: HostRect;
	viewportOffset?: { x: number; y: number };
}

/**
 * Text typed into the host's launcher placeholder before the chat was ready.
 *
 * `focus` is set when the placeholder input still had focus at handoff, so the
 * visitor can keep typing in the chat's own composer.
 */
export interface HostSetDraftMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'set-draft';
	text: string;
	focus: boolean;
}

/**
 * Ask the iframe to move focus to its own launcher (bubble button or sticky-bar
 * field). Sent by the host after the chat closed when the element that opened
 * it is gone. Additive: older iframes ignore it, and the iframe ignores it while
 * the chat is open or when it renders no launcher.
 */
export interface HostFocusLauncherMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'focus-launcher';
}

/**
 * A prompt from an entry point waits in the host's queue behind a running
 * answer (`queued: true`) or no longer does (`queued: false`: sent or dropped).
 * The iframe only shows a status line; it never infers a queue itself. Additive:
 * an older iframe ignores it.
 */
export interface HostQueuedPromptMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'queued-prompt';
	queued: boolean;
}

/**
 * The inline collapse has landed: the iframe removes its inline surface and
 * answers `inline-ended`. Additive: an older iframe never gets it (no
 * `inline` capability).
 */
export interface HostInlineEndMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'inline-end';
}

export type HostToIframeMessage =
	| HostCloseRequestMessage
	| HostInlineEndMessage
	| HostExpandedMessage
	| HostOpenRequestMessage
	| HostOpenWithMessageMessage
	| HostSendMessageMessage
	| HostNewChatMessage
	| HostClearHistoryMessage
	| HostSetViewMessage
	| HostSetLauncherStateMessage
	| HostSetSafeAreaMessage
	| HostPageContextMessage
	| HostSetDraftMessage
	| HostFocusLauncherMessage
	| HostQueuedPromptMessage
	| HostOriginRectMessage;

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

/**
 * Whether `data` is a chat protocol message sent by the iframe.
 * @param data `MessageEvent.data`.
 */
export function isIframeToHostMessage(
	data: unknown
): data is IframeToHostMessage {
	if ( ! data || typeof data !== 'object' ) {
		return false;
	}
	const msg = data as Record< string, unknown >;
	return msg.type === MESSAGE_PREFIX;
}

/**
 * Whether `data` is a chat protocol message sent by the host.
 * @param data `MessageEvent.data`.
 */
export function isHostToIframeMessage(
	data: unknown
): data is HostToIframeMessage {
	if ( ! data || typeof data !== 'object' ) {
		return false;
	}
	const msg = data as Record< string, unknown >;
	return msg.type === MESSAGE_PREFIX;
}

/**
 * Sanitise the optional `origin` of an open command. A malformed value (not an
 * object, non-finite `x` or `y`) yields `undefined` and never rejects the
 * message: the open still happens with the default origin.
 * @param value
 */
export function sanitizeOpenOrigin(
	value: unknown
): HostOpenOrigin | undefined {
	if ( ! value || typeof value !== 'object' ) {
		return undefined;
	}
	const { x, y } = value as Record< string, unknown >;
	if ( typeof x !== 'number' || typeof y !== 'number' ) {
		return undefined;
	}
	return Number.isFinite( x ) && Number.isFinite( y ) ? { x, y } : undefined;
}

/**
 * Sanitise the optional `explicitVariant` flag of an open command: only a
 * literal `true` counts, and only together with a valid variant.
 * @param msg An `open-request` or `open-with-message` command.
 */
export function isExplicitVariant( msg: object ): boolean {
	const flags = msg as Record< string, unknown >;
	return (
		flags.explicitVariant === true &&
		( flags.variant === 'default' ||
			flags.variant === 'sidebar' ||
			flags.variant === 'fullscreen' ||
			flags.variant === 'island' )
	);
}

const isFiniteNumber = ( v: unknown ): v is number =>
	typeof v === 'number' && Number.isFinite( v );

/**
 * Whether `value` is a well-formed {@link HostRect}.
 * @param value
 */
export function isHostRect( value: unknown ): value is HostRect {
	if ( ! value || typeof value !== 'object' ) {
		return false;
	}
	const r = value as Record< string, unknown >;
	return (
		isFiniteNumber( r.left ) &&
		isFiniteNumber( r.top ) &&
		isFiniteNumber( r.width ) &&
		isFiniteNumber( r.height ) &&
		isFiniteNumber( r.radius ) &&
		r.width >= 0 &&
		r.height >= 0 &&
		r.radius >= 0
	);
}

/**
 * Whether `value` is a well-formed {@link HostMorphOpen}. Only the required
 * parts are checked; a malformed value means "open without morph".
 * @param value
 */
export function isHostMorphOpen( value: unknown ): value is HostMorphOpen {
	if ( ! value || typeof value !== 'object' ) {
		return false;
	}
	const m = value as Record< string, unknown >;
	if ( m.presentation !== 'morph' || ! isHostRect( m.originRect ) ) {
		return false;
	}
	if (
		m.placement !== undefined &&
		m.placement !== 'center' &&
		m.placement !== 'corner' &&
		m.placement !== 'sheet'
	) {
		return false;
	}
	if ( m.viewportOffset !== undefined ) {
		const o = m.viewportOffset as Record< string, unknown > | null;
		if (
			! o ||
			typeof o !== 'object' ||
			! isFiniteNumber( o.x ) ||
			! isFiniteNumber( o.y )
		) {
			return false;
		}
	}
	return true;
}

/**
 * Sanitise the optional `inline` field of an open command: a malformed value
 * means "not inline". The radius is clamped to 0..64 px.
 * @param value
 */
export function sanitizeInlineOpen(
	value: unknown
): HostInlineOpen | undefined {
	if ( ! value || typeof value !== 'object' ) {
		return undefined;
	}
	const { radius } = value as Record< string, unknown >;
	if ( ! isFiniteNumber( radius ) ) {
		return undefined;
	}
	return { radius: Math.max( 0, Math.min( 64, radius ) ) };
}

/**
 * Narrow an optional `capabilities` field to the capabilities this build
 * knows. Anything malformed yields an empty list.
 * @param value
 */
export function sanitizeCapabilities( value: unknown ): IframeCapability[] {
	if ( ! Array.isArray( value ) ) {
		return [];
	}
	return IFRAME_CAPABILITIES.filter( ( cap ) => value.includes( cap ) );
}

/**
 * Whether `data` is an iframe `morph-started` message.
 * @param data
 */
export function isMorphStartedMessage(
	data: unknown
): data is IframeMorphStartedMessage {
	return (
		isIframeToHostMessage( data ) &&
		( data as { action?: unknown } ).action === 'morph-started'
	);
}

/**
 * Whether `data` is an iframe `morph-landed` message.
 * @param data
 */
export function isMorphLandedMessage(
	data: unknown
): data is IframeMorphLandedMessage {
	return (
		isIframeToHostMessage( data ) &&
		( data as { action?: unknown } ).action === 'morph-landed'
	);
}

/**
 * Whether `data` is a well-formed iframe `surface-rect` message.
 * @param data
 */
export function isSurfaceRectMessage(
	data: unknown
): data is IframeSurfaceRectMessage {
	if ( ! isIframeToHostMessage( data ) ) {
		return false;
	}
	const m = data as unknown as Record< string, unknown >;
	return (
		m.action === 'surface-rect' &&
		isHostRect( m.rect ) &&
		( m.phase === 'settled' || m.phase === 'closing' )
	);
}

/**
 * Whether `data` is a well-formed host `origin-rect` message.
 * @param data
 */
export function isOriginRectMessage(
	data: unknown
): data is HostOriginRectMessage {
	if ( ! isHostToIframeMessage( data ) ) {
		return false;
	}
	const m = data as unknown as Record< string, unknown >;
	return m.action === 'origin-rect' && isHostRect( m.rect );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The chat iframe is served from the host page's own origin, so both sides
 * pin postMessage to it and drop messages from any other origin.
 * @param event
 */
export function isTrustedOrigin( event: MessageEvent ): boolean {
	return event.origin === window.location.origin;
}

/**
 * Send a message from the iframe to the host (parent window).
 * @param message
 */
export function sendToHost( message: IframeToHostMessage ): void {
	if ( window.parent && window.parent !== window ) {
		window.parent.postMessage( message, window.location.origin );
	}
}

/**
 * Send a message from the host to the iframe.
 * @param iframe
 * @param message
 */
export function sendToIframe(
	iframe: HTMLIFrameElement,
	message: HostToIframeMessage
): void {
	if ( iframe.contentWindow ) {
		iframe.contentWindow.postMessage( message, window.location.origin );
	}
}

/**
 * Check if the current window is inside an iframe.
 */
export function isInIframe(): boolean {
	try {
		return window.self !== window.top;
	} catch ( e ) {
		// If accessing window.top throws (cross-origin), we are in an iframe
		return true;
	}
}
